import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../context/AppContext";
import { getDashboardData } from "../services/companyServices";
import { CONFIG } from "../config/general";

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const calculateValidityPercent = (start, end) => {
  if (!start || !end) return 0;
  const total = new Date(end) - new Date(start);
  const used = Date.now() - new Date(start);
  return Math.min(100, Math.max(0, Math.round((used / total) * 100)));
};

const Dashboard = () => {
  const { login } = useContext(AppContext);
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState({});

  useEffect(() => {
    getDashboardData(login.token).then((res) => {
      if (res?.success) setData(res.data);
    });
  }, []);

  const key = data.key || {};
  const sync = data.sync || {};
  const adminVer = data.latestAdminVersion;
  const userVer = data.latestUserVersion;

  const validityPercent = useMemo(
    () => calculateValidityPercent(key.createdAt, key.expiringAt),
    [key.createdAt, key.expiringAt]
  );

  return (
    <div className="customer-dashboard min-vh-100 p-3">
      <div className="container-fluid" style={{ maxWidth: 1522 }}>

        {/* ================= SUBSCRIPTION CARD ================= */}
        <div className="card mb-4 gradient-card">
          <div className="card-body p-4 tableData">

            <div className="row mb-4">
              <div className="col-md-8">
                <h5 className="text-primary-custom mb-2">Subscription Key</h5>

                <div className="position-relative d-inline-block copyKey" style={{ maxWidth: "100%" }}>
                  <p className="font-monospace keyBg p-2 rounded text-truncate pe-5 mb-0">
                    {key.key || "No Key"}
                  </p>

                  <button
                    type="button"
                    className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-1"
                    onClick={() => {
                      if (!key.key) return;
                      navigator.clipboard.writeText(key.key);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1500);
                    }}
                    title="Copy"
                  >
                    <span className="material-symbols-outlined fs-5">
                      {copied ? "check" : "content_copy"}
                    </span>
                  </button>

                </div>
              </div>

              <div className="col-md-4 text-md-end mt-3 mt-md-0">
                <span className="status-badge">
                  <span className="status-dot"></span>
                  {key.status || "Inactive"}
                </span>
              </div>
            </div>

            <div className="row g-4 mb-4">
              <div className="col-md-4">
                <p className="text-muted-custom small mb-2">Expires At</p>
                <h2 className="display-7 fw-medium mb-0">
                  {formatDate(key.expiringAt)}
                </h2>
              </div>

              <div className="col-md-4">
                <p className="text-muted-custom small mb-2">Subscription Type</p>
                <h2 className="display-7 fw-medium mb-0">
                  {key.keyType || "Standard"}
                </h2>
              </div>

              <div className="col-md-4 text-md-end">
                <p className="text-muted-custom small mb-2">Total Cost</p>
                <h2 className="display-6 fw-medium text-primary-custom mb-0">
                  ₹{key.total || 0}
                </h2>
              </div>
            </div>

            {/* Validity */}
            <div className="mb-4">
              <div className="d-flex justify-content-between text-muted-custom small mb-2">
                <span>Validity</span>
                <span>{validityPercent}%</span>
              </div>
              <div className="progress">
                <div
                  className="progress-bar"
                  style={{ width: `${validityPercent}%` }}
                  role="progressbar"
                />
              </div>
            </div>

            {/* Features */}
            <div className="row g-3">
              {key.calling && <Feature label="Calling" icon="phone_in_talk" />}
              {key.tracking && <Feature label="Tracking" icon="history_toggle_off" />}
              {key.advertisment && <Feature label="Advertisement" icon="ad_group" />}
            </div>

          </div>
        </div>

        {/* ================= SYNC STATS ================= */}
        <div className="mb-5">
          <h2 className="h4 fw-medium mb-3">Synced Data</h2>
          <div className="row g-3 g-sm-4">
            <Stat title="Users" value={sync.user} icon="people" />
            <Stat title="Groups" value={sync.group} icon="groups" />
            <Stat title="Departments" value={sync.department} icon="corporate_fare" />
            <Stat title="Tracking" value={sync.tracking} icon="share_location" />
            <Stat title="Version" value={sync.version} icon="verified" />
            <Stat title="Last Sync" value={formatDate(sync.syncAt)} icon="schedule" />
          </div>
        </div>

        {/* ================= MESSAGES ================= */}
        <h2 className="h4 fw-medium mb-3">Messages Overview</h2>
        <div className="row g-3 gx-4 mb-5">
          <OverviewCard
            title="Private Messages"
            icon="chat_bubble"
            stats={[
              { label: "Texts", value: sync.messages?.private?.text || 0 },
              { label: "Files", value: sync.messages?.private?.file || 0 },
            ]}
          />
          <OverviewCard
            title="Group Messages"
            icon="groups"
            stats={[
              { label: "Texts", value: sync.messages?.group?.text || 0 },
              { label: "Files", value: sync.messages?.group?.file || 0 },
            ]}
          />
        </div>

        {/* ================= CALLS ================= */}
        <h2 className="h4 fw-medium mb-3">Calls Overview</h2>
        <div className="row g-3 gx-4">
          <OverviewCard
            title="Private Calls"
            icon="call"
            stats={[{ value: sync.call?.private?.call || 0 }]}
          />
          <OverviewCard
            title="Group Calls"
            icon="call_log"
            stats={[{ value: sync.call?.group?.call || 0 }]}
          />
        </div>

        {/* ================= APP LATEST VERSION ================= */}
        <h2 className="h4 fw-medium mb-3 mt-5">Latest App Versions</h2>

        <div className="row g-3 gx-4">
          <AppVersionCard
            title="Admin Application"
            icon="admin_panel_settings"
            version={adminVer?.version || "N/A"}
            downloads={adminVer}
          />

          <AppVersionCard
            title="User Application"
            icon="groups"
            version={userVer?.version || "N/A"}
            downloads={userVer}
          />
        </div>


      </div>
    </div>
  );
};

const AppVersionCard = ({ title, icon, version, downloads }) => {
  const hasAny =
    downloads?.windowsFile || downloads?.macFile || downloads?.linuxFile;

  return (
    <div className="col-md-6">
      <div className="card shadow-sm stat-card h-100">
        <div className="card-body p-4 d-flex flex-column">

          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-medium mb-0">{title}</h5>
            <span className="material-symbols-outlined text-primary-custom fs-1">
              {icon}
            </span>
          </div>

          {/* Version */}
          <div className="mb-3">
            <p className="text-muted-custom small mb-1">Latest Version</p>
            <h3 className="display-7 fw-medium mb-0">{version}</h3>
          </div>

          {/* Downloads */}
          <div className="mt-auto">
            {hasAny ? (
              <div className="d-flex gap-2 flex-wrap">
                {downloads?.windowsFile && (
                  <a
                    href={`${CONFIG.REACT_APP_UPLOADS}/${downloads.windowsFile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm"
                    title="Download for Windows"
                  >
                    <span className="material-symbols-outlined me-1">
                      desktop_windows
                    </span>
                    Windows
                  </a>
                )}

                {downloads?.macFile && (
                  <a
                    href={`${CONFIG.REACT_APP_UPLOADS}/${downloads.macFile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm"
                    title="Download for macOS"
                  >
                    <span className="material-symbols-outlined me-1">
                      laptop_mac
                    </span>
                    macOS
                  </a>
                )}

                {downloads?.linuxFile && (
                  <a
                    href={`${CONFIG.REACT_APP_UPLOADS}/${downloads.linuxFile}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-outline-primary btn-sm"
                    title="Download for Linux"
                  >
                    <span className="material-symbols-outlined me-1">
                      terminal
                    </span>
                    Linux
                  </a>
                )}
              </div>
            ) : (
              <p className="text-muted-custom small mb-0">
                No downloads available
              </p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

/* ================= SMALL COMPONENTS ================= */

const Feature = ({ label, icon }) => (
  <div className="col-6 col-sm-4 col-md-3">
    <div className="feature-box d-flex align-items-center gap-2">
      <span className="material-symbols-outlined">{icon}</span>
      <p className="small fw-medium mb-0 fs-20">{label}</p>
    </div>
  </div>
);

const Stat = ({ title, value, icon }) => (
  <div className="col-6 col-sm-4 col-lg-2">
    <div className="card shadow-sm stat-card">
      <div className="card-body text-center p-3">
        <span className="material-symbols-outlined text-primary-custom fs-1 mb-2">
          {icon}
        </span>
        <h3 className="h4 fw-medium mb-0">{value ?? 0}</h3>
        <p className="text-muted-custom small mb-0">{title}</p>
      </div>
    </div>
  </div>
);

const OverviewCard = ({ title, icon, stats }) => (
  <div className="col-md-6">
    <div className="card shadow-sm stat-card">
      <div className="card-body p-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h5 className="fw-medium mb-0">{title}</h5>
          <span className="material-symbols-outlined text-primary-custom fs-1">
            {icon}
          </span>
        </div>
        <div className="d-flex gap-5">
          {stats.map((s, i) => (
            <div key={i}>
              <h3 className="display-7 fw-medium mb-0">{s.value}</h3>
              {s.label && (
                <p className="text-muted-custom small mb-0">{s.label}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default Dashboard;
